import type { RefObject } from 'react'

import { useEffect, useRef, useState } from 'react'

interface UseInViewOptions {
    once?: boolean
    rootMargin?: string
    threshold?: number
}

interface UseInViewResult<T extends Element> {
    ref: RefObject<T | null>
    inView: boolean
}

// Reports whether an element is on screen. Starts as false on the server and the first client render,
// so markup always matches during hydration.
export const useInView = <T extends Element = HTMLDivElement>({
    once = true,
    rootMargin = '0px 0px -10% 0px',
    threshold = 0
}: UseInViewOptions = {}): UseInViewResult<T> => {
    const ref = useRef<T | null>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true)

                if (once) observer.disconnect()
            }
            else if (!once) {
                setInView(false)
            }
        }, { rootMargin, threshold })

        observer.observe(element)

        return () => observer.disconnect()
    }, [once, rootMargin, threshold])

    return { ref, inView }
}
